import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, BookOpen, Users, Clock, Target, Download, Calendar, TrendingUp, Award, BarChart3, Brain, ArrowRight, MessageCircle, Lightbulb, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { programLevels, howWeTeach, whyParentsTrust, RAZORPAY_PAYMENT_LINK } from '../data/programsData';

const Programs = () => {
  const navigate = useNavigate();

  const handleBookOrientation = () => {
    window.location.href = RAZORPAY_PAYMENT_LINK;
  };

  const handleDownloadCurriculum = () => {
    // Open PDF from clean domain URL in new tab
    window.open('/financial-markets-program-curriculum.pdf', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-5 flex justify-between items-center">
          <div 
            onClick={() => navigate('/')}
            className="font-serif text-2xl text-blue-900 font-semibold cursor-pointer hover:text-blue-800 transition-colors"
          >
            Financial Markets Program
          </div>
          <div className="flex gap-6 items-center">
            <button
              onClick={() => navigate('/')}
              className="text-gray-700 hover:text-blue-900 transition-colors"
            >
              Home
            </button>
            <Button
              onClick={handleBookOrientation}
              className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-2 transition-colors"
            >
              Book Orientation
            </Button>
          </div>
        </div>
      </header>

      {/* Premium Journey Hero */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial from-blue-800/20 via-transparent to-transparent"></div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-block bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 px-6 py-2 rounded-full text-sm font-bold uppercase tracking-wide mb-6">
            3-Level Progressive Curriculum
          </div>
          
          <h1 className="font-serif text-5xl md:text-6xl text-white mb-6 leading-tight">
            Your Teen's Journey:<br />From Financial Novice to Young Analyst
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
            A structured 30-week transformation that builds real financial intelligence, step by systematic step.
          </p>

          <div className="flex items-center justify-center gap-8 text-white/80 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <TrendingUp size={20} className="text-amber-400" />
              <span>Progressive Mastery</span>
            </div>
            <span className="hidden sm:block">•</span>
            <div className="flex items-center gap-2">
              <Award size={20} className="text-amber-400" />
              <span>Certificate Per Level</span>
            </div>
            <span className="hidden sm:block">•</span>
            <div className="flex items-center gap-2">
              <Target size={20} className="text-amber-400" />
              <span>Real-World Skills</span>
            </div>
          </div>
        </div>
      </section>

      {/* Premium Journey Roadmap - 3 Levels */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white relative">
        
        {/* Vertical Journey Line (hidden on mobile) */}
        <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-300 via-purple-300 to-amber-300 transform -translate-x-1/2 opacity-30"></div>

        <div className="max-w-6xl mx-auto space-y-16">
          
          {/* LEVEL 1: FOUNDATION */}
          <div className="relative">
            {/* Connector Dot */}
            <div className="hidden md:block absolute left-1/2 top-20 w-6 h-6 bg-blue-500 rounded-full border-4 border-white transform -translate-x-1/2 z-10 shadow-lg"></div>
            
            <div className="relative">
              {/* Level Number Badge */}
              <div className="flex justify-center md:justify-start mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-3xl shadow-xl ring-4 ring-blue-200">
                  01
                </div>
              </div>

              {/* Premium Card */}
              <Card className="p-8 md:p-10 bg-white border-4 border-blue-500 shadow-2xl rounded-2xl relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50 rounded-full -mr-32 -mt-32 opacity-50"></div>
                
                <div className="relative z-10">
                  {/* Header */}
                  <div className="mb-8">
                    <div className="inline-block bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-bold mb-4 uppercase tracking-wide">
                      Foundation Level
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-3">
                      From Zero to Financial Basics
                    </h2>
                    <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                      Your teen goes from financially clueless to confidently understanding money, markets, and smart decisions.
                    </p>
                  </div>

                  {/* Skills Unlocked */}
                  <div className="mb-8 p-5 md:p-6 bg-blue-50 rounded-xl border-2 border-blue-200">
                    <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                      <Award className="text-blue-600" size={24} />
                      Skills Unlocked
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Money Mindset Mastery</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Chart Reading Basics</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Banking & Saving Expert</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Market Fundamentals</span>
                      </div>
                    </div>
                  </div>

                  {/* Transformation */}
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="p-5 bg-red-50 rounded-lg border-2 border-red-200">
                      <p className="text-sm font-bold text-red-900 mb-2 uppercase">Before</p>
                      <p className="text-gray-700 text-sm">❌ Spends money without thinking<br/>❌ Confused by financial news<br/>❌ No understanding of investing</p>
                    </div>
                    <div className="p-5 bg-green-50 rounded-lg border-2 border-green-200">
                      <p className="text-sm font-bold text-green-900 mb-2 uppercase">After</p>
                      <p className="text-gray-700 text-sm">✅ Budgets intelligently<br/>✅ Reads market charts<br/>✅ Explains stocks confidently</p>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Clock size={18} className="text-blue-600" />
                      <span>20 Live Sessions</span>
                    </div>
                    <span className="hidden sm:inline">•</span>
                    <div className="flex items-center gap-2">
                      <Calendar size={18} className="text-blue-600" />
                      <span>10 Weeks</span>
                    </div>
                    <span className="hidden sm:inline">•</span>
                    <div className="flex items-center gap-2">
                      <Users size={18} className="text-blue-600" />
                      <span>Max 20 Students</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Arrow Connector */}
              <div className="flex justify-center my-8">
                <ArrowRight size={48} className="text-blue-400 transform rotate-90" />
              </div>
            </div>
          </div>

          {/* LEVEL 2: APPLIED */}
          <div className="relative">
            {/* Connector Dot */}
            <div className="hidden md:block absolute left-1/2 top-20 w-6 h-6 bg-purple-500 rounded-full border-4 border-white transform -translate-x-1/2 z-10 shadow-lg"></div>
            
            <div className="relative">
              {/* Level Number Badge */}
              <div className="flex justify-center md:justify-start mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white font-bold text-3xl shadow-xl ring-4 ring-purple-200">
                  02
                </div>
              </div>

              {/* Premium Card */}
              <Card className="p-8 md:p-10 bg-white border-4 border-purple-500 shadow-2xl rounded-2xl relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full -mr-32 -mt-32 opacity-50"></div>
                
                <div className="relative z-10">
                  {/* Header */}
                  <div className="mb-8">
                    <div className="inline-block bg-purple-100 text-purple-900 px-4 py-2 rounded-full text-sm font-bold mb-4 uppercase tracking-wide">
                      Applied Level
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-3">
                      From Learning to Doing
                    </h2>
                    <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                      Theory becomes practice. Your teen starts analyzing real companies and building actual investment strategies.
                    </p>
                  </div>

                  {/* Skills Unlocked */}
                  <div className="mb-8 p-5 md:p-6 bg-purple-50 rounded-xl border-2 border-purple-200">
                    <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                      <BarChart3 className="text-purple-600" size={24} />
                      Skills Unlocked
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Financial Statement Decoder</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Stock Analysis Pro</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Investment Strategy Builder</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Company Research Expert</span>
                      </div>
                    </div>
                  </div>

                  {/* Transformation */}
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="p-5 bg-red-50 rounded-lg border-2 border-red-200">
                      <p className="text-sm font-bold text-red-900 mb-2 uppercase">Before</p>
                      <p className="text-gray-700 text-sm">❌ Theory without practice<br/>❌ Can't read financial statements<br/>❌ Passive knowledge only</p>
                    </div>
                    <div className="p-5 bg-green-50 rounded-lg border-2 border-green-200">
                      <p className="text-sm font-bold text-green-900 mb-2 uppercase">After</p>
                      <p className="text-gray-700 text-sm">✅ Analyzes real companies<br/>✅ Decodes balance sheets<br/>✅ Builds investment plans</p>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Clock size={18} className="text-purple-600" />
                      <span>25 Live Sessions</span>
                    </div>
                    <span className="hidden sm:inline">•</span>
                    <div className="flex items-center gap-2">
                      <Calendar size={18} className="text-purple-600" />
                      <span>10 Weeks</span>
                    </div>
                    <span className="hidden sm:inline">•</span>
                    <div className="flex items-center gap-2">
                      <Users size={18} className="text-purple-600" />
                      <span>Max 20 Students</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Arrow Connector */}
              <div className="flex justify-center my-8">
                <ArrowRight size={48} className="text-purple-400 transform rotate-90" />
              </div>
            </div>
          </div>

          {/* LEVEL 3: ADVANCED */}
          <div className="relative">
            {/* Connector Dot */}
            <div className="hidden md:block absolute left-1/2 top-20 w-6 h-6 bg-amber-500 rounded-full border-4 border-white transform -translate-x-1/2 z-10 shadow-lg"></div>
            
            <div className="relative">
              {/* Level Number Badge */}
              <div className="flex justify-center md:justify-start mb-6">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-yellow-600 flex items-center justify-center text-slate-900 font-bold text-3xl shadow-xl ring-4 ring-amber-200">
                  03
                </div>
              </div>

              {/* Premium Card */}
              <Card className="p-8 md:p-10 bg-white border-4 border-amber-500 shadow-2xl rounded-2xl relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-amber-50 rounded-full -mr-32 -mt-32 opacity-50"></div>
                
                <div className="relative z-10">
                  {/* Header */}
                  <div className="mb-8">
                    <div className="inline-block bg-amber-100 text-amber-900 px-4 py-2 rounded-full text-sm font-bold mb-4 uppercase tracking-wide">
                      Advanced Level
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl text-gray-900 mb-3">
                      From Student to Young Analyst
                    </h2>
                    <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                      Your teen thinks like institutional investors, using professional tools and systematic decision-making frameworks.
                    </p>
                  </div>

                  {/* Skills Unlocked */}
                  <div className="mb-8 p-5 md:p-6 bg-amber-50 rounded-xl border-2 border-amber-200">
                    <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2">
                      <Brain className="text-amber-600" size={24} />
                      Skills Unlocked
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Advanced Technical Analysis</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Trading Plan Architect</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Institutional Thinking</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                        <span>Portfolio Management</span>
                      </div>
                    </div>
                  </div>

                  {/* Transformation */}
                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="p-5 bg-red-50 rounded-lg border-2 border-red-200">
                      <p className="text-sm font-bold text-red-900 mb-2 uppercase">Before</p>
                      <p className="text-gray-700 text-sm">❌ Basic understanding only<br/>❌ No systematic approach<br/>❌ Casual observer mindset</p>
                    </div>
                    <div className="p-5 bg-green-50 rounded-lg border-2 border-green-200">
                      <p className="text-sm font-bold text-green-900 mb-2 uppercase">After</p>
                      <p className="text-gray-700 text-sm">✅ Professional-level analysis<br/>✅ Systematic trading plans<br/>✅ Institutional thinking</p>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Clock size={18} className="text-amber-600" />
                      <span>20 Live Sessions</span>
                    </div>
                    <span className="hidden sm:inline">•</span>
                    <div className="flex items-center gap-2">
                      <Calendar size={18} className="text-amber-600" />
                      <span>10 Weeks</span>
                    </div>
                    <span className="hidden sm:inline">•</span>
                    <div className="flex items-center gap-2">
                      <Users size={18} className="text-amber-600" />
                      <span>Max 20 Students</span>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Final Achievement Badge */}
              <div className="flex justify-center mt-12">
                <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 text-slate-900 px-8 md:px-10 py-5 rounded-2xl shadow-2xl border-4 border-yellow-400">
                  <div className="flex items-center gap-3">
                    <Award size={32} className="text-slate-900 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-lg md:text-xl">Journey Complete!</p>
                      <p className="text-sm">Your teen is now a financially intelligent young adult</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Visual Curriculum Showcase - Placeholder */}
      <section className="py-16 px-6 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl text-center mb-4">
            Real Curriculum Depth
          </h2>
          <p className="text-center text-gray-400 mb-12 max-w-2xl mx-auto">
            Sample slides from our live sessions showcasing structured frameworks and professional content
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Slide 1: Market Philosophy - KEEP EXISTING */}
            <Card className="bg-gray-800 border border-gray-700 overflow-hidden flex items-center justify-center p-4">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/hpsiu7yd_MARKET%20PHILOSOPHY%20-%20SLIDE%201.png"
                alt="Market Philosophy - Money Moves Markets"
                className="w-full h-auto object-contain max-h-[400px]"
              />
            </Card>

            {/* Slide 2: Macro Made Easy - NEW */}
            <Card className="bg-gray-800 border border-gray-700 overflow-hidden flex items-center justify-center p-4">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/0iquqepp_Screenshot%20%2814%29.png"
                alt="Macro Made Easy - How Big Money Decides Markets"
                className="w-full h-auto object-contain max-h-[400px]"
              />
            </Card>

            {/* Slide 3: What is a Sector - NEW */}
            <Card className="bg-gray-800 border border-gray-700 overflow-hidden flex items-center justify-center p-4">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/dmjfmbdv_Screenshot%20%284%29.png"
                alt="What is a Sector - Technology, Healthcare, Automobiles"
                className="w-full h-auto object-contain max-h-[400px]"
              />
            </Card>
          </div>
        </div>
      </section>

      {/* How We Teach - PREMIUM OPTION 4 */}
      <section className="py-20 px-6 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl text-gray-900 mb-4">
              Our Teaching Philosophy
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Why traditional methods fail—and what we do instead
            </p>
          </div>

          <div className="space-y-12">
            
            {/* Method 1: Live Interactive Sessions */}
            <Card className="p-10 md:p-12 bg-white border-2 border-blue-200 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl">
              <div className="flex flex-col md:flex-row md:items-start gap-8">
                {/* Number Badge */}
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-3xl shadow-lg ring-4 ring-blue-100">
                    01
                  </div>
                </div>
                
                {/* Content */}
                <div className="flex-1">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <MessageCircle size={32} className="text-blue-600" />
                      <h3 className="text-2xl font-bold text-gray-900">
                        Live Interactive Sessions
                      </h3>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Not pre-recorded videos—real-time discussions where every student's voice matters. Instant Q&A, live feedback, and active participation throughout.
                    </p>
                  </div>
                  
                  <div className="pt-6 border-t-2 border-blue-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap size={20} className="text-amber-500" />
                      <p className="font-bold text-gray-900 uppercase text-sm tracking-wide">The Outcome</p>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      <strong className="text-blue-900">85% higher engagement</strong> than recorded classes. Students report this as their "most engaging class ever"—they actually look forward to sessions.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Method 2: Real Market Data */}
            <Card className="p-10 md:p-12 bg-white border-2 border-green-200 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl">
              <div className="flex flex-col md:flex-row md:items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center text-white font-bold text-3xl shadow-lg ring-4 ring-green-100">
                    02
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <TrendingUp size={32} className="text-green-600" />
                      <h3 className="text-2xl font-bold text-gray-900">
                        Real Market Data & Live Examples
                      </h3>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Not theoretical case studies from textbooks. Students analyze actual stocks, real financial statements, and current market movements as they happen.
                    </p>
                  </div>
                  
                  <div className="pt-6 border-t-2 border-green-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap size={20} className="text-amber-500" />
                      <p className="font-bold text-gray-900 uppercase text-sm tracking-wide">The Outcome</p>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      Teens develop <strong className="text-green-900">real-world financial literacy</strong>—not just exam knowledge. They can immediately apply skills to understand news, family investments, and personal finances.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Method 3: Structured Curriculum */}
            <Card className="p-10 md:p-12 bg-white border-2 border-purple-200 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl">
              <div className="flex flex-col md:flex-row md:items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold text-3xl shadow-lg ring-4 ring-purple-100">
                    03
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Target size={32} className="text-purple-600" />
                      <h3 className="text-2xl font-bold text-gray-900">
                        Systematic Progressive Curriculum
                      </h3>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Not random YouTube topics. A carefully designed 30-week journey that builds from foundation to advanced—each concept scaffolds on the previous one.
                    </p>
                  </div>
                  
                  <div className="pt-6 border-t-2 border-purple-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap size={20} className="text-amber-500" />
                      <p className="font-bold text-gray-900 uppercase text-sm tracking-wide">The Outcome</p>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      <strong className="text-purple-900">Zero knowledge gaps.</strong> Students don't get lost or overwhelmed. They progress confidently from novice to young analyst with clear milestones.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Method 4: Small Batch Learning */}
            <Card className="p-10 md:p-12 bg-white border-2 border-amber-200 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl">
              <div className="flex flex-col md:flex-row md:items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-slate-900 font-bold text-3xl shadow-lg ring-4 ring-amber-100">
                    04
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Users size={32} className="text-amber-600" />
                      <h3 className="text-2xl font-bold text-gray-900">
                        Small Batch, Maximum Attention
                      </h3>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Limited to 20 students per cohort—not 100+ student lecture halls. Every teen gets personalized feedback, individual attention, and space to ask questions without hesitation.
                    </p>
                  </div>
                  
                  <div className="pt-6 border-t-2 border-amber-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap size={20} className="text-amber-500" />
                      <p className="font-bold text-gray-900 uppercase text-sm tracking-wide">The Outcome</p>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      <strong className="text-amber-900">3x more personal interaction</strong> than traditional classes. No student gets left behind or ignored. Questions get answered, concepts get clarified.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Method 5: Practical Application */}
            <Card className="p-10 md:p-12 bg-white border-2 border-rose-200 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl">
              <div className="flex flex-col md:flex-row md:items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center text-white font-bold text-3xl shadow-lg ring-4 ring-rose-100">
                    05
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Award size={32} className="text-rose-600" />
                      <h3 className="text-2xl font-bold text-gray-900">
                        Hands-On Practical Application
                      </h3>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      Not just listening to lectures. Students create actual trading plans, analyze real companies, build investment strategies, and present their findings—applying every concept immediately.
                    </p>
                  </div>
                  
                  <div className="pt-6 border-t-2 border-rose-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap size={20} className="text-amber-500" />
                      <p className="font-bold text-gray-900 uppercase text-sm tracking-wide">The Outcome</p>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      <strong className="text-rose-900">Skills that stick.</strong> Theory becomes muscle memory. Teens retain what they learn because they've practiced it, not just memorized it for exams.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Method 6: Critical Thinking Framework */}
            <Card className="p-10 md:p-12 bg-white border-2 border-indigo-200 shadow-xl hover:shadow-2xl transition-all duration-300 rounded-2xl">
              <div className="flex flex-col md:flex-row md:items-start gap-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold text-3xl shadow-lg ring-4 ring-indigo-100">
                    06
                  </div>
                </div>
                
                <div className="flex-1">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <Brain size={32} className="text-indigo-600" />
                      <h3 className="text-2xl font-bold text-gray-900">
                        Critical Thinking Over Memorization
                      </h3>
                    </div>
                    <p className="text-lg text-gray-700 leading-relaxed">
                      We teach teenagers HOW to think about money—not WHAT to think. Frameworks for decision-making, risk assessment, and systematic analysis they'll use for life.
                    </p>
                  </div>
                  
                  <div className="pt-6 border-t-2 border-indigo-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap size={20} className="text-amber-500" />
                      <p className="font-bold text-gray-900 uppercase text-sm tracking-wide">The Outcome</p>
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      <strong className="text-indigo-900">Financial intelligence for life.</strong> Not just "good at finance class"—teens develop mental models they'll use in every money decision for decades.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

          </div>
        </div>
      </section>

      {/* Why Parents Trust This Program */}
      <section className="py-16 px-6 bg-blue-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl text-gray-900 mb-4 text-center">
            Why Parents Trust This Program
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Built on principles of education, discipline, and long-term thinking
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyParentsTrust.map((reason, index) => (
              <Card key={index} className="p-6 bg-white border border-gray-200">
                <div className="flex items-start gap-3">
                  <CheckCircle size={24} className="text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {reason.title}
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      {reason.description}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What Students Actually Learn - Curriculum Showcase */}
      <section className="py-16 px-6 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-serif text-4xl text-gray-900 mb-3 text-center">
            What Students Actually Learn
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
            Real slides from our live curriculum — from foundational concepts to advanced technical analysis
          </p>

          {/* Curriculum Grid - All Images */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            
            {/* Slide 1 - Technical Analysis Intro */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/0iquqepp_Screenshot%20%2814%29.png"
                alt="Macro Economics Made Easy"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-gradient-to-br from-purple-50 to-white">
                <h3 className="font-semibold text-gray-900 text-sm">Macroeconomics Simplified</h3>
              </div>
            </div>

            {/* Slide 2 - Limit Orders */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/ztobyvs7_Screenshot%20%2842%29.png"
                alt="When to Use Limit Orders"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-gradient-to-br from-blue-50 to-white">
                <h3 className="font-semibold text-gray-900 text-sm">Order Types & Execution</h3>
              </div>
            </div>

            {/* Slide 3 - Market Philosophy */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/hpsiu7yd_MARKET%20PHILOSOPHY%20-%20SLIDE%201.png"
                alt="Market Philosophy - Foundation thinking"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-gradient-to-br from-green-50 to-white">
                <h3 className="font-semibold text-gray-900 text-sm">Market Philosophy & Mindset</h3>
              </div>
            </div>

            {/* Slide 4 - Range Trading */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/mx8gmcp6_RANGE%20TRADING%20-%20SLIDE%203.png"
                alt="Range Trading strategies"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-gradient-to-br from-amber-50 to-white">
                <h3 className="font-semibold text-gray-900 text-sm">Range Trading Strategies</h3>
              </div>
            </div>

            {/* Slide 5 - Trading Plan */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/ia0wpvp5_TRADING%20PLAN%20-%20SLIDE2.png"
                alt="Trading Plan development"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-gradient-to-br from-pink-50 to-white">
                <h3 className="font-semibold text-gray-900 text-sm">Building a Trading Plan</h3>
              </div>
            </div>

            {/* Slide 6 - Advanced Technical Analysis */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/qyduemle_Screenshot%20%2825%29.png"
                alt="Introduction to Advanced Technical Analysis"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-gradient-to-br from-teal-50 to-white">
                <h3 className="font-semibold text-gray-900 text-sm">Advanced Technical Analysis</h3>
              </div>
            </div>

            {/* Slide 7 - Cash Flow Statement */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/feaf5eif_Screenshot%20%2822%29.png"
                alt="Cash Flow Statement - Sweet Delight Chocolate Co."
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-gradient-to-br from-orange-50 to-white">
                <h3 className="font-semibold text-gray-900 text-sm">Financial Statement Analysis</h3>
              </div>
            </div>

            {/* Slide 8 - Stop Loss */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/v5ldtenj_Screenshot%20%2855%29.png"
                alt="What is a Stop-Loss?"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-gradient-to-br from-purple-50 to-white">
                <h3 className="font-semibold text-gray-900 text-sm">Risk Management Essentials</h3>
              </div>
            </div>

            {/* Slide 9 - Flag Chart Pattern */}
            <div className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/c8cm24jj_Screenshot%20%2844%29.png"
                alt="Flag Chart Pattern analysis"
                className="w-full h-56 object-cover"
              />
              <div className="p-4 bg-gradient-to-br from-indigo-50 to-white">
                <h3 className="font-semibold text-gray-900 text-sm">Chart Pattern Recognition</h3>
              </div>
            </div>

          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center bg-white rounded-xl p-8 shadow-lg max-w-3xl mx-auto">
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
              This Is Just a Preview
            </h3>
            <p className="text-gray-700 mb-6 leading-relaxed">
              The full 10-week program includes <strong>20 live sessions</strong>, worksheets, 
              hands-on exercises, and real-time market analysis. Every concept is taught using 
              interactive visuals, case studies, and practical examples.
            </p>
            <p className="text-gray-600 text-sm">
              Download the detailed curriculum PDF to see the complete week-by-week breakdown.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Book a parent orientation session to learn more about the program structure, 
            curriculum, and enrollment process.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleBookOrientation}
              className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-6 text-lg transition-colors"
            >
              Book Parent Orientation – ₹299
            </Button>
            <Button
              onClick={handleDownloadCurriculum}
              className="bg-white hover:bg-gray-100 text-blue-900 px-10 py-6 text-lg transition-colors flex items-center justify-center gap-2"
            >
              <Download size={20} />
              Download Detailed Curriculum
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="font-serif text-2xl mb-4">
              Advanced Financial Markets Program
            </h3>
            <p className="text-gray-400">
              Building the next generation of financially literate leaders
            </p>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-sm text-gray-400 mb-4">
              <strong>Disclaimer:</strong> Educational program only. Not investment advice.
            </p>
            <p className="text-xs text-gray-500 mb-6 max-w-3xl mx-auto leading-relaxed">
              This program is for educational purposes only and does not constitute investment advice, stock recommendations, portfolio management, or financial advisory services.
            </p>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Advanced Financial Markets Program. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Programs;
